import { cloneDeep } from 'lodash-es';
import debugDispatcher from '../../utils/debugDispatcher';
import persistentStorage from '../../utils/persistentStorage';
import {
  TProperties,
  TPropsSuscriptor,
  TUpdateFieldConfiguration,
} from './types';
import { PropsSelectorUndefinedObject } from './usePropsSelector';

const propsLog = 'propsLog';

interface IPropsStoreConf {
  logCommands: {
    propsStore?: string;
    updateProp?: string;
    propsSuscriptors?: string;
    propsLog?: string;
  };
}

const registeredCommands: string[] = [];

type TFieldId = string | number;

export class PropsStore<
  PropsType extends Record<TFieldId, unknown> = Record<TFieldId, unknown>,
> {
  log = persistentStorage[propsLog];

  fields: Record<TFieldId, PropsType> = {};

  suscriptors: Record<TFieldId, TPropsSuscriptor<PropsType>[]> = {};

  loggers: Record<string, (props: unknown[]) => void> = {
    propsLog: ([log]) => {
      this.log = log;
      persistentStorage[propsLog] = log;
    },
    propsStore: ([fieldId]) => {
      if (fieldId) console.info(this.fields[fieldId as TFieldId]);
      else console.info(this.fields);
    },
    propsSuscriptors: ([fieldId]) => {
      if (fieldId) console.info(this.suscriptors[fieldId as TFieldId]);
      else console.info(this.suscriptors);
    },
    updateProp: ([fieldId, newProps]) => {
      this.updateField(fieldId as TFieldId, newProps as PropsType);
    },
  };

  constructor(private configuration?: IPropsStoreConf) {
    if (this.configuration) {
      for (const command of Object.values(this.configuration.logCommands)) {
        if (registeredCommands.includes(command))
          throw new Error(
            `There are two propsStore registered with the same command: ${command}`,
          );
      }

      if (this.configuration.logCommands.propsStore)
        debugDispatcher.on(
          this.configuration.logCommands.propsStore,
          this.loggers.propsStore,
          'Muestra el contenido actual del propsStore',
        );
      if (this.configuration.logCommands.updateProp)
        debugDispatcher.on(
          this.configuration.logCommands.updateProp,
          this.loggers.updateProp,
          'dd.updateProp(fieldId, newProps). El objeto newProps será mergeado sobre las props actuales',
        );
      if (this.configuration.logCommands.propsSuscriptors)
        debugDispatcher.on(
          this.configuration.logCommands.propsSuscriptors,
          this.loggers.propsSuscriptors,
          'Muestra los suscriptores actuales del propsStore',
        );
      if (this.configuration.logCommands.propsLog)
        debugDispatcher.on(
          this.configuration.logCommands.propsLog,
          this.loggers.propsLog,
          'Si se pasa como true, hace log de las acciones. Si se pasa como trace(fieldId), se hace log de todas las acciones y trace del campo especificado. Si se pasa trace(), hace log y trace de todas las acciones.',
        );
    }
  }

  destructor() {
    if (this.configuration) {
      if (this.configuration.logCommands.propsStore)
        debugDispatcher.off(
          this.configuration.logCommands.propsStore,
          this.loggers.propsStore,
        );
      if (this.configuration.logCommands.updateProp)
        debugDispatcher.off(
          this.configuration.logCommands.updateProp,
          this.loggers.updateProp,
        );
      if (this.configuration.logCommands.propsSuscriptors)
        debugDispatcher.off(
          this.configuration.logCommands.propsSuscriptors,
          this.loggers.propsSuscriptors,
        );
      if (this.configuration.logCommands.propsLog)
        debugDispatcher.off(
          this.configuration.logCommands.propsLog,
          this.loggers.propsLog,
        );
    }
  }

  getAllFields() {
    return cloneDeep(this.fields);
  }

  /**
   * Devuelve los props actuales de un campo.
   */
  getFieldProps<ParsedPropsType = PropsType>(fieldId: TFieldId) {
    return this.fields[fieldId] as unknown as ParsedPropsType;
  }

  removeField(fieldId: TFieldId) {
    delete this.fields[fieldId];
    if (this.log) console.info(`propsStore: removeNode ${fieldId}`);
  }

  /**
   * Permite establecer un suscriptor que será llamado
   * cada vez que las props del campo especificado cambien.
   */
  subscribe(fieldId: TFieldId, callback: TPropsSuscriptor<PropsType>) {
    if (!this.suscriptors[fieldId]) this.suscriptors[fieldId] = [];

    this.suscriptors[fieldId].push(callback);

    callback(this.fields[fieldId] ?? PropsSelectorUndefinedObject);

    return () => {
      this.suscriptors[fieldId] = this.suscriptors[fieldId].filter(
        (current) => current !== callback,
      );
    };
  }

  /**
   * Actualiza o crea las props de un campo.
   *
   * La tercera prop está relacionada
   */
  updateField<
    NewPropsType extends Record<TFieldId, unknown> = Partial<PropsType>,
  >(
    fieldId: TFieldId,
    props: Partial<NewPropsType>,
    conf?: TUpdateFieldConfiguration,
  ) {
    if (fieldId === undefined) return;

    const { noEmit, isUrgent } = conf ?? {};
    const newProps = { ...this.fields[fieldId], ...props };
    this.fields[fieldId] = newProps;

    if (this.log === true || this.log === fieldId)
      console.info({ fieldId, props });
    if (this.log === 'trace') console.trace();
    else {
      const logId =
        typeof this.log === 'string' &&
        (this.log.match(/trace\(([^)]+)\)/) ?? [])[1];
      if (fieldId === logId) console.trace();
    }

    if (!noEmit) {
      this.suscriptors[fieldId]?.forEach((current) =>
        current(newProps, isUrgent),
      );
      this.suscriptors.any?.forEach((current) =>
        current({ ...newProps, fieldId }, isUrgent),
      );
    }
  }
}

const propsStore = new PropsStore<TProperties>({
  logCommands: {
    propsLog: 'propsLog',
    propsStore: 'propsStore',
    propsSuscriptors: 'propsSuscriptors',
    updateProp: 'updateProp',
  },
});

export default propsStore;
